import React, { useState } from "react";
import { Typography, Box, Skeleton, useTheme } from "@mui/material";
import HighchartsReact from "highcharts-react-official";
import Highcharts from "highcharts";
import { useGetRiskTrendQuery } from '../../../store/slices/apis/riskDashboardSlice';
import RiskPopup from "./RiskPopup";
import { useParams } from "react-router-dom";

const borderRadius = require("highcharts-border-radius");
borderRadius(Highcharts);

const RiskTrendGraph = () => {
    const { projectId } = useParams();
    const [popupData, setPopupData] = useState(null);
    const [open, setOpen] = useState(false);
    const theme = useTheme();

    const { data: RiskTrend = [], isLoading } = useGetRiskTrendQuery({
        project_id: projectId
    });

    const yAxisLabels = [0, 5, 10, 15, 20, 25];
    const maxval = yAxisLabels[yAxisLabels.length - 1];


    const sliceHeight = 3; // हर slice की height
    const getStackedSlices = (value) => {
        const fullSlices = Math.floor(value / sliceHeight);
        const remainder = value % sliceHeight;
        const slices = new Array(fullSlices).fill(sliceHeight);
        if (remainder > 0) slices.push(remainder);
        return slices;
    };

    const categories = RiskTrend?.data?.map(item => item.month) || [];


    const series = [];

    const maxSlices = Math.ceil(maxval / sliceHeight);

    for (let i = 0; i < maxSlices; i++) {
        series.push({
            name: `Slice ${i + 1}`,
            type: 'column',
            data: RiskTrend?.data?.map(item => {
                const slices = getStackedSlices(item.riskCount);
                return slices[i] || 0;
            }),
            borderRadiusTopLeft: i === maxSlices - 1 ? 10 : 0,
            borderRadiusTopRight: i === maxSlices - 1 ? 10 : 0,
            color: '#415385',
            pointWidth: 50,
            dataLabels: { enabled: false },
            stack: 'risk'
        });
    }

    const options = {
        chart: {
            type: "column",
        },
        title: { text: "" },
        xAxis: {
            categories: RiskTrend?.data?.map(item => item.month) || [],
            tickmarkPlacement: 'on',
            tickWidth: 1,
            tickLength: 10,
            tickColor: '#999',
            lineWidth: 1,
            lineColor: '#ccc',
            labels: {
                style: {
                    fontWeight: 500,
                    fontSize: '12px',
                    color: '#000000',
                    opacity: 0.5,
                    lineHeight: '100%',
                },
            },
        },

        yAxis: {
            min: 0,
            gridLineWidth: 1,
            tickWidth: 1,
            tickLength: 10,
            tickColor: '#999',
            lineWidth: 1,
            lineColor: "#ccc",

            beginAtZero: true,
            title: {
                text: "",
            },
            tickPositioner: function () {
                return yAxisLabels;
            },
        },
        tooltip: {
            formatter: function () {
                const total = this.points.reduce((sum, point) => sum + point.y, 0);
                const category = categories[this.x];
                return `<b>${category}</b><br/>Total Risk Count: <b>${total}</b>`;
            },
            shared: true,
        },
        plotOptions: {
            column: {
                stacking: 'normal',
                pointPadding: 0.15,
                groupPadding: 0.3,
                borderWidth: 1
            },
            series: {
                cursor: 'pointer',
                point: {
                    events: {
                        click: function () {
                            setPopupData({
                                category: categories[this.x],
                                value: this.y,
                            });
                            setOpen(true);
                        },
                    }
                }
            }
        },
        legend: { enabled: false },
        credits: { enabled: false },
        series: series
    };

    const getDateRangeFromMonthString = (monthStr) => {
        const monthMap = { Jan: 0, Feb: 1, Mar: 2, Apr: 3, May: 4, Jun: 5, Jul: 6, Aug: 7, Sep: 8, Oct: 9, Nov: 10, Dec: 11 };
        const monthName = monthStr.slice(0, 3);
        const yearSuffix = monthStr.slice(3);
        const year = parseInt('20' + yearSuffix);
        const monthIndex = monthMap[monthName];
        const startDate = new Date(year, monthIndex, 1);
        const endDate = new Date(year, monthIndex + 1, 0);
        const formatDate = (date) => `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}-${String(date.getDate()).padStart(2, '0')}`;
        return { dateRange: `${formatDate(startDate)},${formatDate(endDate)}` };
    };

    if (isLoading) {
        return (
            <Box sx={{ mt: 2, backgroundColor: "white", borderRadius: "5px", padding: '20px' }}>
                <Skeleton variant="text" width={200} height={28} />
                <Skeleton variant="rectangular" width={660} height={250} />
            </Box>
        );
    }


    return (
        <Box sx={{ mt: 0, backgroundColor: "white", width: "100", border: "1px solid #D8D8D8", borderRadius: "5px", padding: "16px", maxWidth: "100%", height: "510px" }}>
            <Typography variant="body" sx={{ fontWeight: 500, fontSize: "18px", lineHeight: "100%", letterSpacing: '-0.4px', color: `${theme?.customSettings?.themeColor}`, }}>
                Risk Trend
            </Typography>
            <Typography sx={{ mt: "5px", color: "#5052588A", fontSize: "14px", lineHeight: "100%", letterSpacing: '-0.4px', }}>
                Risk trends over time to evaluate mitigation effectiveness and emerging threats
            </Typography>
            <Box mt={4}>
                {RiskTrend?.data?.length === 0 ? (
                    <Box
                        sx={{
                            height: 200,
                            mt: 2,
                            display: 'flex',
                            alignItems: 'center',
                            justifyContent: 'center',
                        }}
                    >
                        <Typography sx={{ fontSize: '16px', color: '#999', fontWeight: 500 }}>
                            No data available to display the chart.
                        </Typography>
                    </Box>
                ) : (
                    <HighchartsReact highcharts={Highcharts} options={options} />
                )}
            </Box>

            {open && (() => {
                const { dateRange } = getDateRangeFromMonthString(popupData.category);
                return <RiskPopup title="Risk Trend" value={dateRange} status="dateRange" onOpen={open} onClose={() => setOpen(false)} />;
            })()}
        </Box>
    );
};

export default RiskTrendGraph;
